/** @jsxRuntime classic */
/** @jsx jsx */
import { useMemo, memo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { css, jsx } from '@emotion/react';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';

import FolderBreadcrumbs from '../FolderBreadcrumbs';
import AssetTree from './AssetTree';
import { useUserQuery } from '../../../actions/UserActions';
import {
  useFolderOrganizationQuery,
  useAssetTree,
} from '../../../actions/FolderActions';

function AssetDialog({
  open,
  onClose,
  data = {},
  singleSelect = false,
  hideFilters = false,
}) {
  const { t } = useTranslation();
  const {
    sources = {},
    folderPrefix: _folderPrefix,
    types = ['folder', 'file', 'transcript', 'document', 'meetings'],
  } = data;

  const { user } = useUserQuery();
  const workspace = user?.workspace;
  const rootFolder = `/${user?.workspace?.rootFolder?.id}`;
  const [folderPrefix, setFolderPrefix] = useState(_folderPrefix || rootFolder);

  const [selectedAssets, setSelectedAssets] = useState(sources);
  const { currentFolder, isLoading: isLoadingFolders } =
    useFolderOrganizationQuery({
      organizationId: workspace?.id,
      folderPrefix,
      isRootFolder: folderPrefix === rootFolder,
    });

  const { assetTree } = useAssetTree();

  const folderStates = useMemo(() => {
    if (!assetTree) {
      return {};
    }
    const folders = assetTree?.folders?.reduce((acc, folder) => {
      acc[`${folder.folder_prefix}${folder.id}`] = { total: 0, selected: 0 };
      return acc;
    }, {});

    const rootFolder = assetTree?.folders?.[0]?.folder_prefix?.split('/')?.[1];
    if (rootFolder) {
      folders[`/${rootFolder}`] = { total: 0, selected: 0 };
    }

    assetTree?.assets.forEach((asset) => {
      const folder = asset.folder_prefix;
      const splits = folder.split('/');

      for (let i = 1; i < splits.length; i++) {
        const folderPrefix = splits.slice(0, i + 1).join('/');
        if (folders[folderPrefix]) {
          folders[folderPrefix].total += 1;
        }
      }
    });

    Object.values(selectedAssets).forEach((asset) => {
      const folder = asset.folder_prefix;
      if (!folder) return;
      const splits = folder.split('/');
      for (let i = 1; i < splits.length; i++) {
        const folderPrefix = splits.slice(0, i + 1).join('/');
        if (folders[folderPrefix]) {
          folders[folderPrefix].selected += 1;
        }
      }
    });

    return folders;
  }, [assetTree, selectedAssets]);

  useEffect(() => {
    if (!open) {
      setSelectedAssets({});
      setFolderPrefix(_folderPrefix || rootFolder);
    }
  }, [open, rootFolder, _folderPrefix]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (types.includes('folder') && types.length === 1) {
          onClose(_folderPrefix);
        } else {
          onClose(selectedAssets);
        }
      }}
      fullWidth
      maxWidth="lg"
    >
      <DialogContent css={css``}>
        <FolderBreadcrumbs
          currentFolder={currentFolder}
          workspace={workspace}
          loading={isLoadingFolders}
          onClick={(e, _folderPrefix) => {
            e.preventDefault();
            e.stopPropagation();
            if (_folderPrefix === '/') {
              setFolderPrefix(rootFolder);
            } else {
              setFolderPrefix(_folderPrefix);
            }
          }}
        />
        <AssetTree
          folderPrefix={folderPrefix}
          selectedAssets={selectedAssets}
          singleSelect={singleSelect}
          hideFilters={hideFilters}
          onSelect={(assets) => {
            setSelectedAssets(assets);
          }}
          dropzone={false}
          onFolderSelect={(folder) => {
            const folderPrefix = folder?.folderPrefix;
            const anySelected = Object.values(selectedAssets).some(
              (a) =>
                a.folder_prefix === folderPrefix ||
                a.folder_prefix?.startsWith(`${folderPrefix}/`)
            );

            const affectedAssets = assetTree.assets.filter(
              (asset) =>
                asset.folder_prefix === folderPrefix ||
                asset.folder_prefix.startsWith(`${folderPrefix}/`)
            );

            if (anySelected) {
              setSelectedAssets((prev) => {
                const newSelectedAssets = { ...prev };
                affectedAssets.forEach((asset) => {
                  if (prev[asset.id]) {
                    delete newSelectedAssets[asset.id];
                  }
                });
                return newSelectedAssets;
              });
            } else {
              setSelectedAssets((prev) => {
                const newSelectedAssets = { ...prev };
                affectedAssets.forEach((asset) => {
                  if (asset.type === 'document') return;
                  newSelectedAssets[asset.id] = asset;
                });
                return newSelectedAssets;
              });
            }
          }}
          onAssetSelect={({ assetId, type }) => {
            if (!types.includes(type)) {
              return;
            }

            if (selectedAssets[assetId]) {
              setSelectedAssets((prev) => {
                if (singleSelect) {
                  return {};
                }

                const newSelectedAssets = { ...prev };
                delete newSelectedAssets[assetId];
                return newSelectedAssets;
              });
            } else {
              setSelectedAssets((prev) => {
                if (singleSelect) {
                  const asset = assetTree.assets.find((a) => a.id === assetId);
                  const folderId = parseInt(
                    asset.folder_prefix.split('/').pop()
                  );
                  return { [asset.id]: { ...asset, type, folderId } };
                }

                const newSelectedAssets = { ...prev };
                const asset = assetTree.assets.find((a) => a.id === assetId);
                const folderId = parseInt(asset.folder_prefix.split('/').pop());
                newSelectedAssets[asset.id] = { ...asset, type, folderId };
                return newSelectedAssets;
              });
            }
          }}
          onLocationChange={(newFolderPrefix) => {
            setFolderPrefix(newFolderPrefix);
          }}
          isModal
          hideOptions
          folderCounter={folderStates}
          types={types}
        />
      </DialogContent>
      <DialogActions>
        <Button
          disabled={
            singleSelect &&
            Object.keys(selectedAssets).length === 0 &&
            !types.includes('folder')
          }
          onClick={() => {
            if (
              types.includes('folder') &&
              !Object.keys(selectedAssets)?.length
            ) {
              onClose({
                [currentFolder?.id]: {
                  type: 'folder',
                  name: currentFolder?.name,
                  folderPrefix,
                  id: currentFolder?.id,
                },
              });
            } else {
              onClose(selectedAssets);
            }
          }}
        >
          {types.includes('folder') && types.length === 1
            ? t('Välj mapp')
            : t('Välj')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default memo(AssetDialog);
